import cx from 'classnames'
import { CSSProperties, PropsWithChildren, useEffect, useState } from 'react'
import Styles from './FrameSkin.module.scss'
import { Caption as FrameCaption } from '../Caption'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Pressable, useHover } from '@react-aria/interactions'
import { CaptionDrawer } from 'components/Design/CaptionTab/CaptionDrawer'
import { setCameraToCurrentFrame } from '../helpers'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { track } from '@tldraw/tldraw'

interface CaptionWrapperProps {
  lines: number
  maxCharacterCount?: number
}

interface CaptionProps {
  position: 'above' | 'below'
  lines: number
  maxCharacterCount?: number
}

const Root = ({ className, ...rest }: PropsWithChildren<{ className?: string; 'data-testid'?: string }>) => (
  <div className={cx(Styles.skin, className)} {...rest} />
)

const Caption = track(
  observer(({ position, lines, maxCharacterCount }: PropsWithChildren<CaptionProps>) => {
    const { designStore, drawersStore } = useRootStore()
    const caption = designStore?.captionsStore?.caption
    const hasCaption = Boolean(caption)
    const editor = useEditor()

    const { hoverProps, isHovered } = useHover({})
    const [ref, setRef] = useState<HTMLDivElement | null>(null)
    const { isMobile } = useResponsiveDesign()

    // Once the caption mounts, wait a small amount of time to ensure
    // rendering has completed and then recenter to frame. Not ideal but the
    // only way I can get a good UX right now
    useEffect(() => {
      if (ref) {
        setTimeout(() => {
          setCameraToCurrentFrame(editor, { isMobile })
        }, 10)
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, caption, editor])

    return (
      <Pressable
        onPress={() => {
          if (isMobile && caption) {
            drawersStore.openDrawer(CaptionDrawer)
          }
        }}
      >
        <div
          id="design-caption"
          data-caption-position={position}
          className={cx(Styles.captionContainer, Styles[position], { [Styles.empty]: !hasCaption })}
          style={{ '--lines-to-clamp': lines } as CSSProperties}
          {...hoverProps}
        >
          <div className={Styles.caption} ref={setRef}>
            <FrameCaption isHovered={isHovered} />
          </div>
          {maxCharacterCount && caption && (
            <div className={Styles.characterCount}>
              Character Count {caption.length}/{maxCharacterCount}
            </div>
          )}
        </div>
      </Pressable>
    )
  }),
)

const CaptionAbove = (props: CaptionWrapperProps) => <Caption position="above" {...props} />

const CaptionBelow = (props: CaptionWrapperProps) => <Caption position="below" {...props} />

export const Skin = {
  Root,
  CaptionAbove,
  CaptionBelow,
}
